import React, { useState } from 'react'
import Logo from '../Assets/Finel-logo-PNG.png'
import { useNavigate, NavLink } from 'react-router-dom'
import ConfirmDialog from './ConfirmDialog'
import {
    IconHome,
    IconReports,
    IconHelpCenter,
    IconRatingSystem,
    IconLogout,
} from './NavIcons'

const AddGoalIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 5h14v14H5z" />
    </svg>
)

const GoalListIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h10M9 12h10M9 17h10M5 7h.01M5 12h.01M5 17h.01" />
    </svg>
)

const UserIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 14a4 4 0 10-8 0m8 0c0 2.21-1.79 4-4 4s-4-1.79-4-4m8 0h3m-3 0H8m11-4v6" />
    </svg>
)

const PlanIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-2.21 0-4 1.343-4 3s1.79 3 4 3 4 1.343 4 3-1.79 3-4 3m0-12V6m0 14v-2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16v14H4z" />
    </svg>
)

const UserListIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m8-6a3 3 0 11-6 0 3 3 0 016 0zM9 8a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
)

const navItemClass = ({ isActive }) =>
    [
        'group flex items-center gap-2.5 rounded-2xl px-3 py-2.5 text-sm font-semibold transition-all duration-200',
        isActive
            ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-md shadow-sky-500/20'
            : 'text-slate-600 hover:bg-sky-50 hover:text-sky-700 hover:translate-x-0.5',
    ].join(' ')

const SideBar = () => {
    const navigate = useNavigate()
    const [logoutOpen, setLogoutOpen] = useState(false)
    const profile = JSON.parse(localStorage.getItem('profile') || '{}')
    const isCompany = profile.role === 'company'

    const performLogout = () => {
        localStorage.clear()
        navigate('/login')
    }

    const navItems = [
        { label: 'Dashboard', to: '/home', icon: <IconHome /> },
        { label: 'Add Goal', to: '/add-goal', icon: <AddGoalIcon /> },
        // { label: 'Goal List', to: '/goal-list', icon: <GoalListIcon /> },
        ...(isCompany
            ? [
                { label: 'Add User', to: '/add-user', icon: <UserIcon /> },
                { label: 'My Plan', to: '/my-plan', icon: <PlanIcon /> },
                { label: 'User List', to: '/user-list', icon: <UserListIcon /> },
            ]
            : []),
        { label: 'My Reports', to: '/my-report', icon: <IconReports /> },
        { label: 'Help Center', to: '/help-center', icon: <IconHelpCenter /> },
        { label: 'Rating System', to: '/google-earth', icon: <IconRatingSystem /> },
    ]

    return (
        <>
            <ConfirmDialog
                open={logoutOpen}
                title="Log out?"
                message="Are you sure you want to log out?"
                confirmLabel="Yes"
                cancelLabel="No"
                onConfirm={() => {
                    setLogoutOpen(false)
                    performLogout()
                }}
                onCancel={() => setLogoutOpen(false)}
            />

            <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-sky-100/80 bg-white/95 px-4 py-4 shadow-2xl shadow-sky-900/5 backdrop-blur-xl md:flex md:flex-col">
                <div className="mb-5 flex items-center justify-center rounded-2xl bg-gradient-to-br from-sky-50 to-white px-3 py-4 ring-1 ring-sky-100">
                    <img src={Logo} className="h-11 w-auto object-contain" alt="Serendipity" />
                </div>

                <nav className="flex-1 space-y-1.5 overflow-y-auto pr-1 text-slate-700">
                    {navItems.map((item) => (
                        <NavLink key={item.to} to={item.to} className={navItemClass}>
                            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/10 text-current shadow-sm ring-1 ring-slate-200/70 transition group-hover:ring-sky-200 [&>svg]:h-5 [&>svg]:w-5">
                                {item.icon}
                            </span>
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="mt-4 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-700 p-3.5 text-white shadow-lg shadow-sky-500/20">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-100">Serendipity</p>
                    <p className="mt-1.5 text-xs leading-5 text-white/90">Track goals and progress with clarity.</p>
                </div>

                <button
                    type="button"
                    onClick={() => setLogoutOpen(true)}
                    className="mt-3 flex shadow-md  w-full items-center gap-2.5 rounded-2xl px-3 py-2.5 text-left text-sm font-semibold text-slate-600 transition-all duration-200 hover:bg-red-50 hover:text-red-600"
                >
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-50 text-current ring-1 ring-slate-200 [&>svg]:h-5 [&>svg]:w-5">
                        <IconLogout />
                    </span>
                    <span>Logout</span>
                </button>
            </aside>
        </>
    )
}

export default SideBar
